import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getAccountLedger, getAccounts } from '@/services/firestore'
import { BookOpen, RefreshCw } from 'lucide-react'

export const Route = createFileRoute('/admin/ledger')({ component: LedgerPage })

function LedgerPage() {
    const [accounts, setAccounts] = useState<any[]>([])
    const [entries, setEntries] = useState<any[]>([])
    const [selectedAccount, setSelectedAccount] = useState('')
    const [loading, setLoading] = useState(true)
    const [loadingEntries, setLoadingEntries] = useState(false)

    useEffect(() => { loadAccounts() }, [])
    const loadAccounts = async () => {
        const data = await getAccounts()
        setAccounts(data)
        setLoading(false)
    }

    const loadLedger = async (accountId: string) => {
        setSelectedAccount(accountId)
        if (!accountId) { setEntries([]); return }
        setLoadingEntries(true)
        const data = await getAccountLedger(accountId)
        setEntries(data)
        setLoadingEntries(false)
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })
    const selectedAcc = accounts.find(a => a.id === selectedAccount)

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="mb-5">
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><BookOpen className="w-6 h-6 text-blue-500" />Account Ledger</h1>
                <p className="text-xs text-slate-500 mt-0.5">View debit/credit entries with running balance for any account</p>
            </div>

            {/* Account Selector */}
            <div className="bg-white rounded-xl border border-slate-200/60 p-4 mb-4 shadow-sm">
                <label className="text-xs font-bold text-slate-600 uppercase block mb-1.5">Select Account</label>
                <select value={selectedAccount} onChange={e => loadLedger(e.target.value)}
                    className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Choose an account...</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name} ({a.type})</option>)}
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : !selectedAccount ? (
                <div className="text-center py-20 text-slate-400"><BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm font-semibold">Select an account to view its ledger</p></div>
            ) : loadingEntries ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : entries.length === 0 ? (
                <div className="text-center py-20 text-slate-400"><p className="text-sm font-semibold">No entries for {selectedAcc?.name}</p></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="font-bold text-slate-700 text-sm">{selectedAcc?.code} — {selectedAcc?.name}</span>
                        <span className="text-xs text-slate-500">{entries.length} entries</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Narration</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Type</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-green-600 uppercase">Debit</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-red-600 uppercase">Credit</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Balance</th>
                        </tr></thead>
                        <tbody>
                            {entries.map((e, i) => (
                                <tr key={e.id} className={`border-b border-slate-50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                    <td className="px-4 py-2.5 text-slate-600 text-xs">{e.date}</td>
                                    <td className="px-4 py-2.5 text-slate-700 text-xs">{e.narration}</td>
                                    <td className="px-4 py-2.5"><span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{e.referenceType?.replace('_', ' ')}</span></td>
                                    <td className="px-4 py-2.5 text-right font-mono text-green-600">{e.debit ? f(e.debit) : '—'}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{e.credit ? f(e.credit) : '—'}</td>
                                    <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-800">{f(e.runningBalance)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
